import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import {
  useAuthenticationState,
  setLoggedOut,
  initiateStaffLogin,
} from "redux/features/auth";

import {
  Container,
  Box,
  Content,
  Message,
  Button,
  Form,
} from "react-bulma-components";
import IconButton from "util/iconButton";
import { FaSignInAlt } from "react-icons/fa";

import TopBar from "components/topbar";

const { Field, Control, Label, Input } = Form;

const defaultPrompts = [["Password", "password"]];

function StaffLogin() {
  const dispatch = useDispatch();
  const [alerts, setAlerts] = useState([]);

  const defaultUsernameParameter = "username";

  const [usernameParameter, setUsernameParameter] = useState(
    defaultUsernameParameter
  );

  const [prompts, setPrompts] = useState(defaultPrompts);
  const [credentials, setCredentials] = useState({});

  const { authenticationFailure } = useAuthenticationState();
  useEffect(() => {
    if (!authenticationFailure) return;
    const errorDetails = authenticationFailure.errorDetails || {};
    const promptRequirements = errorDetails.prompt_requirements || {};
    setAlerts(
      promptRequirements.alerts && promptRequirements.alerts.length > 0
        ? promptRequirements.alerts
        : [authenticationFailure.errorMessage || "Authentication failed"]
    );

    const newUsernameParameter =
      promptRequirements.username_parameter || defaultUsernameParameter;
    setUsernameParameter(newUsernameParameter);

    const newPrompts = promptRequirements.credentials_prompts || defaultPrompts;
    setPrompts(newPrompts);

    const previousCredentials = errorDetails.staff_credentials || {};
    const nextCredentials = {};
    (promptRequirements.state_parameters || []).forEach((p) => {
      nextCredentials[p] = previousCredentials[p] || "";
    });
    nextCredentials[newUsernameParameter] =
      previousCredentials[newUsernameParameter] || "";

    newPrompts.forEach(([prompt, param]) => (nextCredentials[param] = ""));
    setCredentials(nextCredentials);
    dispatch(setLoggedOut());
  }, [authenticationFailure, dispatch]);

  const changeCredential = (credential) => (event) => {
    if (credentials[credential] !== "") setAlerts([]);
    let newCredentials = { ...credentials };
    newCredentials[credential] = event.target.value;
    setCredentials(newCredentials);
  };

  const doLogin = (event) => {
    dispatch(initiateStaffLogin(credentials[usernameParameter], credentials));
  };

  const focusCredentials = (index) => (event) => {
    if (event.key === "Enter") {
      if (index >= prompts.length) doLogin(event);
      else document.querySelector(".credentials-" + index).focus();
    }
  };

  const { uninitialized, checking, loggingIn } = useAuthenticationState();
  const initializing = uninitialized || checking;

  return (
    <div className="login-screen">
      <TopBar
        authStatus={
          initializing
            ? "Initializing ..."
            : loggingIn
            ? "Signing in ..."
            : "Not logged in"
        }
        loading={initializing || loggingIn}
      />
      <div className="login-body">
        <Container className="is-max-desktop">
          {initializing ? null : (
            <Box>
              <Content>
                <p>
                  This portal is for the use of Fields Institute staff only. If
                  you are not a Fields staff member, please{" "}
                  <Link to="/">go here</Link> to access the public portal.
                </p>
              </Content>

              {alerts.map((msg, index) => (
                <Message color="danger" key={"alert" + index}>
                  <Message.Body> {msg} </Message.Body>
                </Message>
              ))}

              <Field key={usernameParameter}>
                <Label>Username</Label>
                <Control>
                  <Input
                    value={credentials[usernameParameter] ?? ""}
                    placeholder="Your Fields staff username"
                    onChange={changeCredential(usernameParameter)}
                    onKeyUp={focusCredentials(0)}
                  />
                </Control>
              </Field>
              {prompts.map(([prompt, param], index) => (
                <Field key={param + "field"}>
                  <Label>{prompt}</Label>
                  <Control>
                    <Input
                      className={"credentials-" + index}
                      type="password"
                      value={credentials[param] ?? ""}
                      onChange={changeCredential(param)}
                      onKeyUp={focusCredentials(1 + index)}
                    />
                  </Control>
                </Field>
              ))}

              <Button.Group position="centered" hasAddons>
                <IconButton
                  className="login-button"
                  color="success"
                  onClick={doLogin}
                  icon={FaSignInAlt}
                >
                  Log In
                </IconButton>
              </Button.Group>
            </Box>
          )}
        </Container>
      </div>
    </div>
  );
}

export default StaffLogin;
